// @flow
import { createSelector } from "reselect"
import type { InputSelector } from "reselect"
import { List, Map } from "immutable"

import type { StoreState } from "common/services/types"
import type { CabinClass } from "common/records/CabinClass"
import { searchModesSelector } from "../branding/selectors"
import { placesUrlsIdsMap } from "../places/placesSelectors"
import SearchPlace from "../../records/SearchPlace"
import MultiPlace from "../../records/MultiPlace"
import Place from "../../records/Place"
import Radius from "../../records/Radius"
import PassengersCount from "../../records/PassengersCount"
import urlParserSelector from "../url/parserSelector"
import MulticityFormData from "../../records/MulticityFormData"
import SalesmanFormData from "../../records/SalesmanFormData"
import SearchFormState from "../../records/SearchFormState"
import { nextDate, setIsDefault, createTimeToStay } from "../../records/SearchDate/utils"
import { PLACE_VALUE_MODES, PLACES_TO_OMIT } from "../../consts/place"
import { getSalesmanDefaultDate } from "../../records/Salesman/utils"

const multicitySelector: InputSelector<StoreState, any, ?string> = (state: StoreState): ?string =>
  state.query.multicity
const salesmanSelector: InputSelector<StoreState, any, ?string> = (state: StoreState): ?string =>
  state.query.nomad
const originUrlSelector: InputSelector<StoreState, any, ?string> = (state: StoreState): ?string =>
  state.params.origin
const destinationUrlSelector: InputSelector<StoreState, any, ?string> = (
  state: StoreState,
): ?string => state.params.destination
const outboundDateSelector: InputSelector<StoreState, any, ?string> = (
  state: StoreState,
): ?string => state.params.outboundDate
const inboundDateSelector: InputSelector<StoreState, any, ?string> = (state: StoreState): ?string =>
  state.params.inboundDate
export const defaultSearchFormSelector: InputSelector<StoreState, any, SearchFormState> = (
  state: StoreState,
): SearchFormState => state.searchForm

const adultPassengersSelector: InputSelector<StoreState, any, ?number> = (
  state: StoreState,
): ?number => state.query.adults || state.query.passengers // for backward compatibility - if someone had bookmarked url with passengers
const childPassengersSelector: InputSelector<StoreState, any, ?number> = (
  state: StoreState,
): ?number => state.query.children
const infantPassengersSelector: InputSelector<StoreState, any, ?number> = (
  state: StoreState,
): ?number => state.query.infants

export const passengersSelector: InputSelector<StoreState, any, PassengersCount> = createSelector(
  [adultPassengersSelector, childPassengersSelector, infantPassengersSelector],
  (adults: ?number, children: ?number, infants: ?number) =>
    new PassengersCount({ adults, children, infants }),
)

export const cabinClassSelector: InputSelector<StoreState, any, ?CabinClass> = (
  state: StoreState,
): ?CabinClass => state.query.cabinClass

// TODO make it from params instead of state:
// const originUrlSelector = (state, props) => props.params.origin
// const destinationUrlSelector = (state, props) => props.params.destination
// const outboundDateSelector = (state, props) => props.params.outboundDate
// const inboundDateSelector = (state, props) => props.params.inboundDate

export const placeUrlToSearchPlace = (
  urlString: string,
  placesMap: Map<string, Place>,
  multicity?: boolean = false,
): ?SearchPlace => {
  let radius
  let string = urlString

  if (string === "anywhere") {
    // TODO translated
    return new SearchPlace({ mode: PLACE_VALUE_MODES.ANYWHERE })
  }

  if (string === "-") {
    return multicity ? new SearchPlace({ mode: PLACE_VALUE_MODES.UNSELECTED }) : null
  }

  const radiusMatch = string.match(/-([0-9]+)km$/)
  if (radiusMatch) {
    radius = parseInt(radiusMatch[1], 10)
    string = string.replace(radiusMatch[0], "")
  }

  const place = placesMap.get(string)
  if (place) {
    if (radiusMatch) {
      return new SearchPlace({
        mode: "radius",
        value: new Radius({
          radius,
          lat: place.lat,
          lng: place.lng,
          closeCity: place,
        }),
      })
    }
    return new SearchPlace({
      mode: "place",
      value: new Place(place.toJS()),
    })
  }

  const matchCoords = string.match(/(-?[0-9.]+)-(-?[0-9.]+)$/)
  if (matchCoords && matchCoords.length === 3) {
    const lat = parseFloat(matchCoords[1])
    const lng = parseFloat(matchCoords[2])
    if (!isNaN(lat) && !isNaN(lng)) {
      return new SearchPlace({
        mode: "radius",
        value: new Radius({ lat, lng, radius }),
      })
    }
  }

  return null
}

export const urlPartToSearchPlaces = (
  urlPart: string = "",
  placesMap: Map<string, Place>,
  multicity?: boolean = false,
) =>
  List(
    urlPart
      .split(",")
      .map(part => placeUrlToSearchPlace(part, placesMap, multicity))
      .filter(Boolean)
      .map(searchPlace => searchPlace.set("isDefault", false)),
  )

export const parseMulticity = (
  multicityString: string,
  defaultSearchForm: SearchFormState,
  // eslint-disable-next-line flowtype/no-weak-types
  urlParser: Object,
  placesMap: Map<string, Place>,
) => {
  let trips = List()
  const multicityStrings = multicityString.split(";")
  multicityStrings.forEach((oneMulticityString, index) => {
    let string = oneMulticityString
    if (string === "-") {
      string = "--~--~--"
    }
    const [originString, destinationString, dateString] = string.split("~")

    let origin
    let destination
    let outboundDate

    if (index === 0) {
      // First row
      if (originString === "--" || originString === "anywhere") {
        //  Default
        origin = defaultSearchForm.origin
      } else if (originString === "-") {
        // Empty
        origin = new MultiPlace()
      } else {
        origin = new MultiPlace({
          places: urlPartToSearchPlaces(originString, placesMap, true),
        })
      }
      if (destinationString === "--") {
        // Default
        destination = defaultSearchForm.destination // new MultiPlace({ places: List([new SearchPlace({mode: PLACE_VALUE_MODES.UNSELECTED})]) })
      } else if (destinationString === "-") {
        // Empty
        destination = new MultiPlace()
      } else {
        destination = new MultiPlace({
          places: urlPartToSearchPlaces(destinationString, placesMap, true),
        })
      }
      outboundDate =
        dateString === "--"
          ? defaultSearchForm.outboundDate
          : setIsDefault(false, urlParser.urlToSearchDate(dateString))
    } else {
      // Other rows
      if (originString === "--") {
        //  Default
        origin = trips.get(index - 1).destination
      } else if (originString === "-") {
        // Empty
        origin = new MultiPlace()
      } else {
        origin = new MultiPlace({
          places: urlPartToSearchPlaces(originString, placesMap, true),
        })
      }
      if (destinationString === "--") {
        //  Default
        destination = new MultiPlace({
          places: List([new SearchPlace({ mode: PLACE_VALUE_MODES.UNSELECTED })]),
        })
      } else if (destinationString === "-") {
        // Empty
        destination = new MultiPlace()
      } else {
        destination = new MultiPlace({
          places: urlPartToSearchPlaces(destinationString, placesMap, true),
        })
      }
      outboundDate =
        dateString === "--"
          ? nextDate(trips.get(index - 1).outboundDate)
          : setIsDefault(false, urlParser.urlToSearchDate(dateString))
    }
    if (index + 1 === multicityStrings.length && destinationString === "--") {
      // Last row destination should be anywhere, because => choose next part of journey...
      destination = new MultiPlace({
        places: List([new SearchPlace({ mode: "anywhere", isDefault: true })]),
      })
    }

    trips = trips.push(
      new MulticityFormData({
        origin,
        destination,
        outboundDate,
      }),
    )
  })
  return defaultSearchForm.set("multicity", trips)
}

const parseSalesman = (
  salesmanString: string,
  defaultSearchForm: SearchFormState,
  urlParser: any,
  placesMap: Map<string, Place>,
) => {
  let trips = List()

  const salesmanStrings = salesmanString.split(";")
  salesmanStrings.forEach((oneSalesmanString, index) => {
    let string = oneSalesmanString
    if (string === "-") {
      string = "--~--~--"
    }
    const [destinationString, dateRangeString, timeOfStayString] = string.split("~")

    let destination
    if (
      (destinationString === "--" || destinationString === "anywhere") &&
      (index === 0 || index === 1)
    ) {
      // Default origin & destination
      destination = defaultSearchForm.origin
    } else if (destinationString === "--" || destinationString === "anywhere") {
      destination = new MultiPlace({
        places: List([new SearchPlace({ mode: PLACE_VALUE_MODES.UNSELECTED })]),
      })
    } else if (destinationString === "-") {
      // Empty
      destination = new MultiPlace()
    } else if (index === 1 && destinationString === "---") {
      destination = trips.getIn([0, "destination"])
    } else {
      destination = new MultiPlace({
        places: urlPartToSearchPlaces(destinationString, placesMap, true),
      })
    }

    const dateRange =
      dateRangeString === "---" || dateRangeString === "--" || dateRangeString === "-"
        ? getSalesmanDefaultDate(index, trips)
        : setIsDefault(false, urlParser.urlToSearchDate(dateRangeString))

    const timeOfStay =
      timeOfStayString === "--" || timeOfStayString === "-"
        ? createTimeToStay(3, 5, true)
        : setIsDefault(false, urlParser.urlToSearchDate(timeOfStayString))

    trips = trips.push(
      new SalesmanFormData({
        destination,
        dateRange,
        timeOfStay,
        dateRangeEnabled: dateRangeString !== "-",
        timeOfStayEnabled: timeOfStayString !== "-",
      }),
    )
  })

  return defaultSearchForm.set(
    "salesman",
    new Map({
      routes: trips,
      isReturnSame: salesmanStrings[1].split("~")[0] === "---",
      setReturnDate: salesmanStrings[1].split("~")[1] !== "---",
    }),
  )
}

// place -> origin/destination
export const createPlace = (fieldType: "origin" | "destination") => (
  placeString: ?string,
  placesMap: Map<string, Place>,
): ?MultiPlace => {
  if (placeString) {
    // Of origin exists, it is either
    if (placeString === "--") {
      // Default value
      return null
    }
    if (placeString === "-") {
      // Or unselected
      return new MultiPlace()
    }
    if (fieldType === "origin" && placeString === "anywhere") {
      // if origin is anywhere, take default value
      return null
    }
    // Or defined
    const places = urlPartToSearchPlaces(placeString, placesMap).filter(
      place => !PLACES_TO_OMIT[fieldType].includes(place.getId()),
    )

    return new MultiPlace({ places })
  }
  // Else will use default value
  return null
}

export const originSelector: InputSelector<StoreState, any, ?MultiPlace> = createSelector(
  [originUrlSelector, placesUrlsIdsMap],
  createPlace("origin"),
)

export const destinationSelector: InputSelector<StoreState, any, ?MultiPlace> = createSelector(
  [destinationUrlSelector, placesUrlsIdsMap],
  createPlace("destination"),
)

export const createSearchForm = (
  origin: ?MultiPlace,
  destination: ?MultiPlace,
  outboundDate: ?string,
  inboundDate: ?string,
  multicity: ?string,
  salesman: ?string,
  passengers: PassengersCount,
  cabinClass: ?CabinClass,
  defaultSearchForm: SearchFormState,
  // eslint-disable-next-line flowtype/no-weak-types
  urlParser: Object,
  placesMap: Map<string, Place>,
  modesEnabled: Map<string, Map<string, boolean>>,
): SearchFormState => {
  if (multicity) {
    let searchForm = parseMulticity(multicity, defaultSearchForm, urlParser, placesMap)
    if (passengers) {
      searchForm = searchForm.set("passengers", passengers)
    }
    if (cabinClass) {
      searchForm = searchForm.set("cabinClass", cabinClass)
    }
    return searchForm
      .setDefaultDateIfPast()
      .setDefaultModeIfDisabled(modesEnabled, placesMap)
      .clearInvalidDates()
  }
  if (salesman) {
    let searchForm = parseSalesman(salesman, defaultSearchForm, urlParser, placesMap)
    if (passengers) {
      searchForm = searchForm.set("passengers", passengers)
    }
    if (cabinClass) {
      searchForm = searchForm.set("cabinClass", cabinClass)
    }
    return searchForm.setDefaultDateIfPast().clearInvalidDates()
  }

  let searchForm = defaultSearchForm
  if (origin && destination) {
    searchForm = searchForm.set("multicity", List())
    searchForm = searchForm.set(
      "salesman",
      new Map({
        routes: new List(),
        isReturnSame: true,
        setReturnDate: false,
      }),
    )
  }
  if (origin) searchForm = searchForm.set("origin", origin)
  if (destination) searchForm = searchForm.set("destination", destination)
  if (outboundDate && outboundDate !== "-") {
    searchForm = searchForm.set(
      "outboundDate",
      setIsDefault(false, urlParser.urlToSearchDate(outboundDate)),
    )
  }
  if (inboundDate && inboundDate !== "-") {
    searchForm = searchForm.set(
      "inboundDate",
      setIsDefault(false, urlParser.urlToSearchDate(inboundDate)),
    )
  }
  if (passengers) {
    searchForm = searchForm.set("passengers", passengers)
  }
  if (cabinClass) {
    searchForm = searchForm.set("cabinClass", cabinClass)
  }
  return searchForm.setDefaultDateIfPast().setDefaultModeIfDisabled(modesEnabled, placesMap)
}

export const searchFormSelector: InputSelector<StoreState, any, SearchFormState> = createSelector(
  [
    originSelector,
    destinationSelector,
    outboundDateSelector,
    inboundDateSelector,
    multicitySelector,
    salesmanSelector,
    passengersSelector,
    cabinClassSelector,
    defaultSearchFormSelector,
    urlParserSelector,
    placesUrlsIdsMap,
    searchModesSelector,
  ],
  (
    origin,
    destination,
    outboundDate,
    inboundDate,
    multicity,
    salesman,
    passengers,
    cabinClass,
    defaultSearchForm,
    urlParser,
    placesMap,
    searchModes,
  ) =>
    createSearchForm(
      origin,
      destination,
      outboundDate,
      inboundDate,
      multicity,
      salesman,
      passengers,
      cabinClass,
      defaultSearchForm,
      urlParser,
      placesMap,
      searchModes,
    ),
)
